import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  hiddenInput: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0,0,0,0)",
    whiteSpace: "nowrap",
    ...shorthands.border("0"),
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "6px",
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "16px",
    paddingRight: "16px",
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1.5px", "dashed", BRAND.border),
    transitionProperty: "border-color, background-color, transform",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "rgba(77,32,81,0.04)",
    },
  },
  dropzoneActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "rgba(77,32,81,0.08)",
    transform: "translateY(-1px)",
  },
  iconWrap: {
    width: "36px",
    height: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  primary: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    fontWeight: 700,
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.76rem",
    color: BRAND.muted,
  },
  fileCard: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  fileIcon: {
    width: "30px",
    height: "30px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.gold,
    color: BRAND.ink,
    ...shorthands.borderRadius("8px"),
    flexShrink: 0,
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    flexGrow: 1,
    minWidth: 0,
  },
  fileName: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileMeta: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    color: BRAND.muted,
  },
  actionBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("999px"),
    transitionProperty: "border-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor("#a1331c"),
      color: "#a1331c",
    },
  },
});

export type UploadedFile = {
  name: string;
  size: number;
  dataUrl?: string;
};

type Props = {
  label?: string;
  file: UploadedFile | null;
  onChange: (file: UploadedFile | null) => void;
  accept?: string;
  hint?: string;
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const readAsDataUrl = (file: File): Promise<string | undefined> =>
  new Promise((resolve) => {
    try {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => resolve(undefined);
      r.readAsDataURL(file);
    } catch {
      resolve(undefined);
    }
  });

const FileDropzone = ({
  label = "Datei hierher ziehen oder klicken",
  file,
  onChange,
  accept = ".pdf,.doc,.docx,image/*",
  hint = "PDF, Word oder Bild — wird mit dem Vertragsentwurf gespeichert.",
}: Props) => {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleSelect = async (selected: File | null) => {
    if (!selected) {
      onChange(null);
      return;
    }
    const dataUrl = await readAsDataUrl(selected);
    onChange({ name: selected.name, size: selected.size, dataUrl });
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    handleSelect(f);
    e.target.value = "";
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0] ?? null;
    handleSelect(f);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  return (
    <div className={styles.root}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onInputChange}
        className={styles.hiddenInput}
        aria-label={label}
      />
      {file ? (
        <div className={styles.fileCard}>
          <span className={styles.fileIcon} aria-hidden>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
              <path d="M14 3v5h5" />
            </svg>
          </span>
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>{file.name}</span>
            <span className={styles.fileMeta}>{formatBytes(file.size)}</span>
          </div>
          <button type="button" className={styles.actionBtn} onClick={() => onChange(null)}>
            Entfernen
          </button>
        </div>
      ) : (
        <div
          className={mergeClasses(styles.dropzone, dragOver && styles.dropzoneActive)}
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <span className={styles.iconWrap}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 16V4M6 10l6-6 6 6" />
              <path d="M4 20h16" />
            </svg>
          </span>
          <span className={styles.primary}>{label}</span>
          <span className={styles.hint}>{hint}</span>
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
